import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { trpc } from '../lib/trpc';
import { canManageVenue } from '@wineryxplorer/shared';
import type { Venue } from '@wineryxplorer/shared';
import { PageContainer } from './PageContainer';
import { ErrorDisplay, NotFoundError } from './ui/ErrorDisplay';
import { SectionLoading } from './ui/LoadingSpinner';
import { useEffect, useRef } from 'react';

interface PermissionGuardProps {
  venueId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const PermissionGuard = ({ venueId, children, fallback }: PermissionGuardProps) => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const unauthorizedLoggedRef = useRef(false);

  const { data: venue, isPending: venueLoading, error: venueError } = trpc.venue.getById.useQuery(
    { id: venueId },
    { enabled: Boolean(venueId) }
  );

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth/signin');
    }
  }, [user, authLoading, navigate]);

  // Log unauthorized access attempts (Requirement 6.2)
  useEffect(() => {
    if (
      !authLoading &&
      !venueLoading &&
      user &&
      venue &&
      !canManageVenue(venue as unknown as Venue, user.role, user.id) &&
      !unauthorizedLoggedRef.current
    ) {
      // Mark as logged to prevent duplicate logging
      unauthorizedLoggedRef.current = true;

      // Log the unauthorized access attempt
      // This is logged on the frontend for immediate feedback
      // The backend will also log this when the user tries to access protected endpoints
      // eslint-disable-next-line no-console
      console.warn(
        `Unauthorized access attempt to venue management: User ${user.id} (${user.role}) tried to access venue ${venueId}`
      );
    }
  }, [authLoading, venueLoading, user, venue, venueId]);

  // Show loading state while checking permissions
  if (authLoading || venueLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer>
          <SectionLoading message="Checking permissions..." />
        </PageContainer>
      </div>
    );
  }

  // Handle venue not found
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (venueError || !venue) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <NotFoundError
            title="Venue Not Found"
            message="The venue you're looking for doesn't exist or has been removed."
          />
        </PageContainer>
      </div>
    );
  }

  // Handle unauthorized access
  if (!user || !canManageVenue(venue as unknown as Venue, user.role, user.id)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <ErrorDisplay
            type="error"
            title="Access Denied"
            message="You don't have permission to manage this venue. Only site administrators, super administrators, and assigned venue administrators can access venue management features."
          >
            <p className="text-sm text-gray-600 mt-2">
              If you believe this is an error, please contact your system administrator.
            </p>
          </ErrorDisplay>
        </PageContainer>
      </div>
    );
  }

  // User has permission, render children
  return <>{children}</>;
};